export type Selection = { branch: any; service: any; agent: any; clear?: boolean };

/**
 * Default agent for a service: the current user when they themselves provide it,
 * otherwise the first entry (normally the prepended "any available agent" option).
 * Pass no alias to always take the first entry.
 */
export function defaultAgentForService(service: any, currentUserAlias?: string | null): any {
  const agents: any[] = service?.agents ?? [];
  if (currentUserAlias) {
    const me = agents.find((a: any) => !a.isWholeService && `${a.id}` === `${currentUserAlias}`);
    if (me) return me;
  }
  return agents[0] ?? null;
}

export function addAnyAgentOption(branch: any, anyAgentLabel: string): any {
  return {
    ...branch,
    services: (branch.services || []).map((service: any) => {
      const agents: any[] = service.agents || [];
      if (agents.some((a: any) => a.isWholeService)) return service;
      return {
        ...service,
        agents: [{ id: service.id, isWholeService: true, firstname: anyAgentLabel }, ...agents],
      };
    }),
  };
}

/**
 * Whether a branch has any services, per the `services_count` the list endpoint
 * returns (as a string, e.g. "0" / "9"). Returns true when the count is absent —
 * we only hide a branch when the API explicitly says it has zero services.
 */
export function branchHasServices(branch: any): boolean {
  return branch?.services_count == null || Number(branch.services_count) !== 0;
}

type SearchApiResult = { users?: any[]; branches?: any[]; services?: any[] };

export type SearchResults = {
  // Branch objects to show in the branch column (discovery order preserved).
  matchedBranches: any[];
  // Users-only search → a per-branch tree restricted to the matched employees and
  // the services they provide. `null` for any other search (caller then shows the
  // branch's FULL services/agents from branch details).
  restricted: Record<string, any> | null;
};

/**
 * Search narrows down WHICH branches are shown. A branch matches when it matches
 * directly, via one of its services, or via one of its employees. We collect the
 * matched branches (preferring the rich entry from `allBranches`, falling back to
 * the branch object carried by the search result).
 *
 * Special case: when the backend returns ONLY users (no branches, no services),
 * the search was effectively an employee lookup — so we also build a restricted
 * tree that shows just those employees and the services they provide. As soon as
 * any branch or service also matches, we don't restrict and the caller falls back
 * to full branch details.
 */
export function buildSearchResults(result: SearchApiResult, allBranches: any[]): SearchResults {
  const { users, branches: foundBranches, services } = result;
  const order: string[] = [];
  const fromResult: Record<string, any> = {};

  const consider = (branchObj: any) => {
    if (branchObj?.id == null) return;
    const id = `${branchObj.id}`;
    if (!(id in fromResult)) {
      fromResult[id] = branchObj;
      order.push(id);
    }
  };

  foundBranches?.forEach(consider);
  services?.forEach(s => consider(s?.branch));
  users?.forEach(u => u?.services?.forEach((s: any) => consider(s?.branch)));

  const matchedBranches = order
    .map(id => allBranches.find(b => `${b.id}` === id) ?? fromResult[id])
    .filter(Boolean);

  const usersOnly =
    (users?.length ?? 0) > 0 && !(foundBranches?.length ?? 0) && !(services?.length ?? 0);

  if (!usersOnly) return { matchedBranches, restricted: null };

  const restricted: Record<string, any> = {};
  const ensureBranch = (branchObj: any) => {
    if (branchObj?.id == null) return undefined;
    const id = `${branchObj.id}`;
    if (!restricted[id]) {
      restricted[id] = { ...(allBranches.find(b => `${b.id}` === id) ?? branchObj), services: [] };
    }
    return restricted[id];
  };
  const ensureService = (bd: any, svc: any) => {
    if (svc?.id == null) return null;
    let entry = bd.services.find((s: any) => s.id === svc.id);
    if (!entry) {
      entry = { ...svc, agents: [] };
      bd.services.push(entry);
    }
    return entry;
  };

  users?.forEach(u =>
    u?.services?.forEach((s: any) => {
      const bd = ensureBranch(s?.branch);
      if (!bd) return;
      const svc = ensureService(bd, s);
      if (svc && !svc.agents.find((a: any) => a.id === u.id)) svc.agents.push(u);
    }),
  );

  return { matchedBranches, restricted };
}
